import { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, FileX, Plus, Printer, Download, Check, Pencil } from 'lucide-react';

import { CaseTopBar } from '../components/editor/CaseTopBar';
import { DocumentsPanel } from '../components/editor/DocumentsPanel';
import { EntityTypePickerPopover } from '../components/editor/EntityTypePickerPopover';
import { DeleteCaseDialog } from '../components/cases/DeleteCaseDialog';
import { DeleteDocumentDialog } from '../components/cases/DeleteDocumentDialog';
import { ReprocessDocumentDialog } from '../components/cases/ReprocessDocumentDialog';
import { Button } from '../components/ui/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getFileIconByName } from '../components/FileIcons';
import {
  casesApi,
  documentsApi,
  redactionApi,
  pstApi,
  dictionaryApi,
  apiHelpers,
  type HtmlContent,
  type RedactionJobStatus,
  type ReprocessMode,
  type TextInRegionResponse,
} from '../lib/api';
import { useReasons } from '../hooks/useReasons';
import { useResultsPanelDock } from '../hooks/useResultsPanelDock';
import type { Case, CaseDocument, DocumentStatus, EntitySuggestion, EntityType, RedactionTool } from '../types';
import { DOCUMENT_STATUS_CONFIG } from '../types';
import { cn } from '../components/ui/utils';
import type { PdfSource } from '../components/pdf/PDFViewer';
import { getAuthTokensService } from '../lib/auth/AuthTokensService';

const ResultsPanel = lazy(() => import('../components/editor/ResultsPanel').then(m => ({ default: m.ResultsPanel })));
const PDFEditor = lazy(() => import('../components/pdf/PDFEditor').then(m => ({ default: m.PDFEditor })));
const DocxEditorView = lazy(() => import('../components/docx/DocxEditorView').then(m => ({ default: m.DocxEditorView })));
const EmailEditorView = lazy(() => import('../components/email/EmailEditorView').then(m => ({ default: m.EmailEditorView })));
const PSTEditorView = lazy(() => import('../components/pst/PSTEditorView').then(m => ({ default: m.PSTEditorView })));
const CaseWizardModal = lazy(() => import('../components/cases/CaseWizardModal').then(m => ({ default: m.CaseWizardModal })));
const CaseDictionarySetupModal = lazy(() => import('../components/cases/CaseDictionarySetupModal').then(m => ({ default: m.CaseDictionarySetupModal })));

// Build the per-document progress structures from the full redaction-job list.
// Shared by the initial case load and the polling refresh so the progress bars
// render in the same paint as the document badges (instead of popping in a beat
// later once a second request resolves).
// Job statuses that carry reviewable detection results. Counts for the sidebar
// progress bar are sourced only from these, so a queued/processing/failed job
// (e.g. a freshly-started or failed reprocess) can't shadow an earlier
// successful job and make the bar read detected=0 / vanish.
const RESULT_STATUSES = new Set(['awaiting_approval', 'approved', 'completed']);

function buildEntityProgress(jobs: RedactionJobStatus[], documents: CaseDocument[]) {
  const docIds = new Set(documents.map((d) => d.id));
  const progressMap = new Map<string, { detected: number; approved: number; rejected: number }>();
  const processing = new Set<string>();
  const failed = new Set<string>();
  const progressByDoc = new Map<string, number>();
  const seenLatest = new Set<string>();

  for (const job of jobs) {
    if (!docIds.has(job.document_id)) continue;

    // Jobs are newest first, so the first job seen per document is its current
    // one. A failed latest job means the last attempt errored out — surface a
    // "Failed" state in the sidebar rather than a stale/zero progress bar.
    if (!seenLatest.has(job.document_id)) {
      seenLatest.add(job.document_id);
      if (job.status === 'failed') failed.add(job.document_id);
    }

    // Track documents with active jobs. Jobs are newest first, so the first
    // active job seen per document is the current one — capture its progress.
    if (job.status === 'queued' || job.status === 'processing' || job.status === 'rescanning') {
      if (!processing.has(job.document_id)) {
        processing.add(job.document_id);
        progressByDoc.set(job.document_id, job.progress ?? 0);
      }
    }

    // Use the latest job per document that actually carries results (jobs are
    // returned newest first). Skipping non-result statuses keeps a queued or
    // failed reprocess from overwriting an earlier successful job's counts —
    // otherwise the sidebar progress bar reads detected=0 and disappears until
    // the doc is clicked (which reloads the awaiting_approval job's suggestions).
    if (RESULT_STATUSES.has(job.status) && !progressMap.has(job.document_id)) {
      progressMap.set(job.document_id, {
        detected: job.entities_detected,
        approved: job.entities_approved,
        rejected: job.entities_rejected,
      });
    }
  }

  return { progressMap, processing, progressByDoc, failed };
}

export function CaseEditorPage() {
  const navigate = useNavigate();
  const { caseId } = useParams<{ caseId: string }>();

  // Case state
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [documents, setDocuments] = useState<CaseDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Document viewer state
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [currentDocument, setCurrentDocument] = useState<CaseDocument | null>(null);
  const [pdfData, setPdfData] = useState<PdfSource | null>(null);
  const [htmlContent, setHtmlContent] = useState<HtmlContent | null>(null);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [isFinalizingPst, setIsFinalizingPst] = useState(false);

  // Panel state
  const [isDocumentsPanelCollapsed, setIsDocumentsPanelCollapsed] = useState(false);
  const {
    dockPosition,
    setDockPosition,
    isCollapsed: isResultsPanelCollapsed,
    toggleCollapse: toggleResultsPanelCollapse,
    panelSize,
    isDragging: isResultsPanelDragging,
    handleMouseDown: handleResultsPanelMouseDown,
    panelRef: resultsPanelRef,
  } = useResultsPanelDock();

  // Entity suggestions state
  const [suggestions, setSuggestions] = useState<EntitySuggestion[]>([]);
  const suggestionsRef = useRef<EntitySuggestion[]>(suggestions);
  suggestionsRef.current = suggestions;
  // Tracks which document the current suggestions belong to (set when suggestions are assigned)
  const suggestionsDocIdRef = useRef<string | null>(null);
  // Tracks which document we're currently loading suggestions for (set when loading starts)
  const loadingDocIdRef = useRef<string | null>(null);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<number | null>(null);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);

  // Entity progress per document (for progress bars in side panel)
  const [entityProgress, setEntityProgress] = useState<Map<string, { detected: number; approved: number; rejected: number }>>(new Map());

  // Track documents with active (queued/processing) jobs for progress indicators
  const [processingDocumentIds, setProcessingDocumentIds] = useState<Set<string>>(new Set());

  // Documents whose latest job failed — shown as a "Failed" state in the side panel
  const [failedDocumentIds, setFailedDocumentIds] = useState<Set<string>>(new Set());

  // Analysis progress (0-100) per actively-processing document, for the analyzing bar
  const [processingProgress, setProcessingProgress] = useState<Map<string, number>>(new Map());

  // loadCase() populates progress from its parallel jobs fetch, so the
  // documents-change effect below should skip the first refresh that
  // immediately follows it to avoid a redundant getJobs round-trip.
  const skipNextProgressRefresh = useRef(false);

  // Reasons state
  const { reasons } = useReasons();
  const [entityReasons, setEntityReasons] = useState<Map<number, string>>(new Map());

  // Upload modal state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Delete case state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeletingCase, setIsDeletingCase] = useState(false);

  // Delete document state
  const [deleteDocumentTarget, setDeleteDocumentTarget] = useState<string | null>(null);
  const [reprocessTarget, setReprocessTarget] = useState<string | null>(null);
  const [isDeletingDocument, setIsDeletingDocument] = useState(false);

  // Download/export loading state
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  // Dictionary modal state
  const [isCaseDictModalOpen, setIsCaseDictModalOpen] = useState(false);
  const [fileDictDocId, setFileDictDocId] = useState<string | null>(null);

  // Load case data
  useEffect(() => {
    if (!caseId) return;

    const loadCase = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch the case and its redaction jobs in parallel so the per-document
        // progress bars paint together with the document badges, rather than
        // popping in a beat later once a second request resolves.
        const [response, jobs] = await Promise.all([
          casesApi.getCase(caseId),
          redactionApi.getJobs({ limit: 1000 }),
        ]);
        setCaseData(response);
        setDocuments(response.documents);

        const { progressMap, processing, progressByDoc, failed } = buildEntityProgress(jobs, response.documents);
        setEntityProgress(progressMap);
        setProcessingDocumentIds(processing);
        setProcessingProgress(progressByDoc);
        setFailedDocumentIds(failed);
        skipNextProgressRefresh.current = true;

        // Auto-select first document if available
        if (response.documents.length > 0) {
          setSelectedDocumentId(response.documents[0].id);
        }
      } catch (err) {
        console.error('Failed to load case:', err);
        setError(apiHelpers.handleError(err));
      } finally {
        setIsLoading(false);
      }
    };

    loadCase();
  }, [caseId]);

  // Load entity progress for all documents (for progress bars in side panel)
  // Also tracks which documents have active (queued/processing) jobs
  const loadEntityProgress = useCallback(async () => {
    if (!documents.length) return;
    try {
      const jobs = await redactionApi.getJobs({ limit: 1000 });
      const { progressMap, processing, progressByDoc, failed } = buildEntityProgress(jobs, documents);
      setEntityProgress(progressMap);
      setProcessingDocumentIds(processing);
      setProcessingProgress(progressByDoc);
      setFailedDocumentIds(failed);
    } catch (err) {
      console.error('Failed to load entity progress:', err);
    }
  }, [documents]);

  useEffect(() => {
    // loadCase() already loaded progress from its parallel jobs fetch — skip the
    // redundant refresh it triggers via the documents-change dependency.
    if (skipNextProgressRefresh.current) {
      skipNextProgressRefresh.current = false;
      return;
    }
    loadEntityProgress();
  }, [loadEntityProgress]);

  // Poll for job progress while there are active (queued/processing/rescanning) jobs
  useEffect(() => {
    if (processingDocumentIds.size === 0) return;
    const interval = setInterval(loadEntityProgress, 3000);
    return () => clearInterval(interval);
  }, [processingDocumentIds.size, loadEntityProgress]);

  // Reload suggestions when the selected document finishes processing/rescanning
  const prevProcessingRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (selectedDocumentId && prevProcessingRef.current.has(selectedDocumentId) && !processingDocumentIds.has(selectedDocumentId)) {
      loadSuggestionsForDocument(selectedDocumentId);
    }
    prevProcessingRef.current = processingDocumentIds;
  }, [processingDocumentIds, selectedDocumentId]);

  // Keep entityProgress in sync with local suggestions for the selected document
  // so the left sidebar progress bars update immediately without waiting for server.
  // Only update when suggestions actually belong to the selected document to avoid
  // briefly writing stale data from the previous document during a switch.
  useEffect(() => {
    if (!selectedDocumentId || suggestions.length === 0) return;
    if (suggestionsDocIdRef.current !== selectedDocumentId) return;
    setEntityProgress(prev => {
      const newMap = new Map(prev);
      newMap.set(selectedDocumentId, {
        detected: suggestions.length,
        approved: suggestions.filter(s => s.status === 'approved').length,
        rejected: suggestions.filter(s => s.status === 'rejected').length,
      });
      return newMap;
    });
  }, [suggestions, selectedDocumentId]);

  // Get child documents for the current document (used for PST containers)
  const childDocuments = useMemo(() => {
    if (!currentDocument) return [];
    return documents.filter((doc) => doc.parent_id === currentDocument.id);
  }, [documents, currentDocument]);

  // Check if current document is a container (PST or ZIP)
  const isContainerDocument = useMemo(() => {
    if (!currentDocument) return false;
    return currentDocument.is_container ||
      (currentDocument.metadata?.is_container && childDocuments.length > 0);
  }, [currentDocument, childDocuments]);

  // Check if current document is an email (EML/MSG) with children
  const isEmailWithChildren = useMemo(() => {
    if (!currentDocument) return false;
    const mimeType = currentDocument.type;
    const emailMimeTypes = ['message/rfc822', 'application/vnd.ms-outlook'];
    return emailMimeTypes.includes(mimeType) && childDocuments.length > 0;
  }, [currentDocument, childDocuments]);

  // Check if all child documents are completed
  const allChildrenCompleted = useMemo(() => {
    if (childDocuments.length === 0) return true;
    return childDocuments.every((doc) => doc.status === 'completed');
  }, [childDocuments]);

  // Load selected document
  useEffect(() => {
    if (!selectedDocumentId) {
      suggestionsDocIdRef.current = null;
      setCurrentDocument(null);
      setPdfData(null);
      setHtmlContent(null);
      setSuggestions([]);
      setCurrentJobId(null);
      setEntityReasons(new Map());
      return;
    }

    const doc = documents.find((d) => d.id === selectedDocumentId);
    if (!doc) return;

    setCurrentDocument(doc);
    loadDocumentContent(doc);
    loadSuggestionsForDocument(selectedDocumentId);
  }, [selectedDocumentId, documents]);

  const loadDocumentContent = async (doc: CaseDocument) => {
    setIsLoadingDocument(true);
    setDocumentError(null);
    setPdfData(null);
    setHtmlContent(null);

    // Get file extension from filename (more reliable than MIME type)
    const fileType = doc.name.split('.').pop()?.toLowerCase();

    try {
      if (fileType === 'pdf') {
        const version = doc.status === 'completed' ? 'redacted' : 'original';
        // Stream via Range requests instead of downloading the whole file first:
        // PDF.js renders the first page after a few KB and lazily fetches the rest,
        // so large PDFs open near-instantly. Pass the auth token as a header since
        // PDF.js bypasses the axios auth interceptor.
        const token = getAuthTokensService().getCurrentCredentials()?.access_token?.token;
        setPdfData({
          url: documentsApi.getDownloadUrl(doc.id, version),
          httpHeaders: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
      } else if (['docx', 'doc', 'eml', 'msg'].includes(fileType || '')) {
        const version = doc.status === 'completed' ? 'redacted' : 'original';
        const content = await documentsApi.getHtmlContent(doc.id, version);
        if (!content.success) {
          throw new Error(content.error_message || 'Failed to load document content');
        }
        setHtmlContent(content);
      } else if (fileType === 'pst' || fileType === 'zip') {
        // Container files (PST, ZIP) - their children are shown in DocumentsPanel
        // No content to load for the container itself
      } else {
        // For other file types, just show a placeholder
        setDocumentError('Preview not available for this file type');
      }
    } catch (err) {
      console.error('Failed to load document:', err);
      setDocumentError(apiHelpers.handleError(err));
    } finally {
      setIsLoadingDocument(false);
    }
  };

  const loadSuggestionsForDocument = async (documentId: string) => {
    loadingDocIdRef.current = documentId;
    setIsLoadingSuggestions(true);
    // Don't clear suggestions here — keep showing previous data until new
    // suggestions arrive to prevent the progress bar from flickering to 0%
    setSelectedSuggestionIndex(null);
    setCurrentJobId(null);

    try {
      // Load the newest job that actually carries reviewable results, using the
      // SAME status set as buildEntityProgress(). Filtering to only
      // 'awaiting_approval' here could grab a stale older job (or skip a newer
      // 'approved'/'completed' one), so the suggestions-sync effect would then
      // overwrite the sidebar progress bar with that stale job's counts — making
      // the count flip to a wrong value the moment a document is opened.
      const jobs = await redactionApi.getJobs({ document_id: documentId });

      // Guard against stale responses if user switched documents while loading
      if (documentId !== loadingDocIdRef.current) return;

      // jobs are returned newest-first, so the first result-bearing job is current.
      const job = jobs.find((j) => RESULT_STATUSES.has(j.status));
      if (job) {
        setCurrentJobId(job.job_id);

        // Load suggestions — pass case_id so the server applies dictionary matching
        const suggestionsResponse = await redactionApi.getSuggestions(job.job_id, { case_id: caseId, includeRejected: true });
        // Check again after async call
        if (documentId !== loadingDocIdRef.current) return;
        const mappedSuggestions = suggestionsResponse.suggestions.map((s) => ({
          index: s.index,
          entityType: s.entity_type,
          text: s.text,
          confidence: s.confidence,
          page: s.page,
          bounds: s.bounds,
          replacementText: s.replacement_text,
          rationale: s.rationale,
          status: s.status,
          // Include messageIndex from page field for PST compatibility
          messageIndex: s.page,
          // Include source field for manual vs AI detection
          source: s.source,
          dictionarySource: s.dictionary_source,
          dictionaryAction: s.dictionary_action,
          manuallyOverridden: s.manually_overridden,
          startPos: s.start_pos,
        }));
        suggestionsDocIdRef.current = documentId;
        setSuggestions(mappedSuggestions);

      } else {
        suggestionsDocIdRef.current = documentId;
        setSuggestions([]);
      }
    } catch (err) {
      console.error('Failed to load suggestions:', err);
      suggestionsDocIdRef.current = documentId;
      setSuggestions([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleRenameCase = useCallback(async (newName: string) => {
    if (!caseId) return;
    const updated = await casesApi.updateCase(caseId, { name: newName });
    setCaseData(prev => prev ? { ...prev, name: updated.name } : prev);
  }, [caseId]);

  const handleDeleteCase = async () => {
    if (!caseId) return;
    setIsDeletingCase(true);
    try {
      await casesApi.deleteCase(caseId);
      navigate('/');
    } catch (err) {
      console.error('Failed to delete case:', err);
      setIsDeletingCase(false);
    }
  };

  const handleDeleteDocument = useCallback((documentId: string) => {
    setDeleteDocumentTarget(documentId);
  }, []);

  const handleConfirmDeleteDocument = useCallback(async () => {
    if (!deleteDocumentTarget) return;
    setIsDeletingDocument(true);
    try {
      await documentsApi.deleteDocument(deleteDocumentTarget);
      setDocuments((prev) => prev.filter((d) => d.id !== deleteDocumentTarget));
      if (selectedDocumentId === deleteDocumentTarget) {
        setSelectedDocumentId(null);
      }
      setDeleteDocumentTarget(null);
    } catch (err) {
      console.error('Failed to delete document:', err);
    } finally {
      setIsDeletingDocument(false);
    }
  }, [deleteDocumentTarget, selectedDocumentId]);

  // Reprocess a single document, steered by the entities the reviewer
  // manually flagged on it. Queues an async job, then marks the doc as
  // processing so the sidebar shows "Analyzing..."; the existing 3s poll
  // (processingDocumentIds.size > 0) clears it and the completion effect
  // reloads suggestions when detection finishes.
  // Clicking the reprocess button opens an explanation dialog first; the
  // actual job is only queued once the reviewer confirms.
  const handleRequestReprocess = useCallback((documentId: string) => {
    setReprocessTarget(documentId);
  }, []);

  const handleConfirmReprocessDocument = useCallback(async (mode: ReprocessMode) => {
    if (!reprocessTarget) return;
    const documentId = reprocessTarget;
    try {
      const result = await redactionApi.reprocessDocument(documentId, mode);
      console.info(`Reprocess queued for ${documentId}: ${result.message}`);
      setProcessingDocumentIds((prev) => new Set([...prev, documentId]));
    } catch (err) {
      console.error('Failed to reprocess document:', err);
    } finally {
      setReprocessTarget(null);
    }
  }, [reprocessTarget]);

  const handleUploadDocuments = () => {
    setIsUploadModalOpen(true);
  };

  const handleDocumentsAdded = async () => {
    // Refresh the documents list
    if (caseId) {
      try {
        const response = await casesApi.getCase(caseId);
        setCaseData(response);
        setDocuments(response.documents);
      } catch (err) {
        console.error('Failed to refresh documents:', err);
      }
    }
  };

  const handleDocumentStatusChange = async (status: DocumentStatus) => {
    if (!selectedDocumentId || !caseId) return;

    try {
      await casesApi.updateDocumentStatus(caseId, selectedDocumentId, status);

      // Update local state
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === selectedDocumentId ? { ...doc, status } : doc
        )
      );
      setCurrentDocument((prev) => (prev ? { ...prev, status } : prev));
    } catch (err) {
      console.error('Failed to update document status:', err);
    }
  };

  const handleDownloadDocument = useCallback(async (documentId: string, version: 'original' | 'redacted') => {
    try {
      const { blob, filename } = await documentsApi.downloadDocument(documentId, version);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed', err);
    }
  }, []);

  const handleDownload = async (version: 'original' | 'redacted', format?: 'pdf') => {
    if (!selectedDocumentId || !currentDocument) return;

    try {
      // Provide fallback filename based on version and document name
      const fallbackFilename = version === 'redacted'
        ? `redacted_${currentDocument.name}`
        : currentDocument.name;
      const { blob, filename } = await documentsApi.downloadDocument(
        selectedDocumentId,
        version,
        fallbackFilename,
        format
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Use filename from Content-Disposition header (handles format conversions like PST -> ZIP)
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download document:', err);
    }
  };

  const handleDownloadCase = async () => {
    if (!caseId) return;
    setIsDownloadingZip(true);
    try {
      const { blob, filename } = await casesApi.downloadCaseAsZip(caseId, 'redacted');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download case as ZIP:', err);
    } finally {
      setIsDownloadingZip(false);
    }
  };

  const handleExportMegaPdf = async () => {
    if (!caseId) return;
    setIsExportingPdf(true);
    try {
      const { blob, filename } = await casesApi.exportCaseAsMegaPdf(caseId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export case as mega-PDF:', err);
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleApprove = async (indices: number[]) => {
    if (!currentJobId) return;

    // Build entity_reasons map for approved entities
    const entityReasonsObj: Record<number, string> = {};
    indices.forEach((index) => {
      const reason = entityReasons.get(index);
      if (reason) {
        entityReasonsObj[index] = reason;
      }
    });

    // Optimistic update
    const prevSuggestions = suggestionsRef.current;
    const indexSet = new Set(indices);
    setSuggestions((prev) =>
      prev.map((s) => {
        if (!indexSet.has(s.index)) return s;
        const reason = entityReasons.get(s.index);
        return {
          ...s,
          status: 'approved' as const,
          replacementText: reason ? `[${reason}]` : s.replacementText,
        };
      })
    );

    try {
      await redactionApi.approveSuggestions(currentJobId, {
        approved_entities: indices,
        entity_reasons: entityReasonsObj,
        apply_redactions: false,
      });
      loadEntityProgress();
    } catch (err) {
      console.error('Failed to approve suggestions:', err);
      setSuggestions(prevSuggestions);
    }
  };

  const handleReject = async (indices: number[]) => {
    if (!currentJobId) return;

    // Optimistic update
    const prevSuggestions = suggestionsRef.current;
    const indexSet = new Set(indices);
    setSuggestions((prev) =>
      prev.map((s) =>
        indexSet.has(s.index) ? { ...s, status: 'rejected' as const } : s
      )
    );

    try {
      await redactionApi.approveSuggestions(currentJobId, {
        approved_entities: [],
        rejected_entities: indices,
        entity_reasons: {},
        apply_redactions: false,
      });
      loadEntityProgress();
    } catch (err) {
      console.error('Failed to reject suggestions:', err);
      setSuggestions(prevSuggestions);
    }
  };

  const handleReasonChange = useCallback((index: number, reason: string) => {
    setEntityReasons((prev) => {
      const newMap = new Map(prev);
      if (reason) {
        newMap.set(index, reason);
      } else {
        newMap.delete(index);
      }
      return newMap;
    });
  }, []);

  // Approve a single suggestion with an inline reason (called from PDF hover tooltip)
  const handleApproveWithReason = useCallback(async (index: number, reason?: string) => {
    if (!currentJobId) return;
    if (reason) {
      handleReasonChange(index, reason);
    }
    // Optimistic update
    const prevSuggestions = suggestionsRef.current;
    setSuggestions(prev =>
      prev.map(s =>
        s.index === index
          ? { ...s, status: 'approved' as const, replacementText: reason ? `[${reason}]` : s.replacementText }
          : s
      )
    );
    try {
      const entityReasonsObj: Record<number, string> = {};
      if (reason) entityReasonsObj[index] = reason;
      await redactionApi.approveSuggestions(currentJobId, {
        approved_entities: [index],
        entity_reasons: entityReasonsObj,
        apply_redactions: false,
      });
    } catch (err) {
      console.error('Failed to approve suggestion:', err);
      setSuggestions(prevSuggestions);
    }
  }, [currentJobId, handleReasonChange]);

  // Reject a single suggestion (called from PDF hover tooltip dismiss)
  const handleRejectSingle = (index: number) => handleReject([index]);

  // Delete a manual entity completely (used for blackout page toggle-off)
  const handleDeleteManualEntity = useCallback(async (index: number) => {
    if (!currentJobId) return;
    // Optimistic update
    const prevSuggestions = suggestionsRef.current;
    setSuggestions(prev => prev.filter(s => s.index !== index));
    try {
      await redactionApi.deleteManualEntity(currentJobId, index);
    } catch (err) {
      console.error('Failed to delete manual entity:', err);
      setSuggestions(prevSuggestions);
    }
  }, [currentJobId]);

  // Restore a rejected entity to pending (called from PDF hover tooltip on rejected entities)
  const handleRestoreToPending = useCallback(async (index: number) => {
    if (!currentJobId) return;
    // Optimistic update
    const prevSuggestions = suggestionsRef.current;
    setSuggestions(prev =>
      prev.map(s =>
        s.index === index
          ? { ...s, status: 'pending' as const, replacementText: undefined }
          : s
      )
    );
    try {
      await redactionApi.approveSuggestions(currentJobId, {
        approved_entities: [],
        pending_entities: [index],
        entity_reasons: {},
        apply_redactions: false,
      });
    } catch (err) {
      console.error('Failed to restore suggestion to pending:', err);
      setSuggestions(prevSuggestions);
    }
  }, [currentJobId]);

  // Change the entity type of an existing entity from the redact box.
  const handleChangeEntityType = useCallback(async (index: number, newType: EntityType) => {
    if (!currentJobId) return;
    // Optimistic update
    const prevSuggestions = suggestionsRef.current;
    setSuggestions(prev =>
      prev.map(s => (s.index === index ? { ...s, entityType: newType } : s))
    );
    try {
      await redactionApi.updateEntityType(currentJobId, index, newType);
    } catch (err) {
      console.error('Failed to change entity type:', err);
      setSuggestions(prevSuggestions);
    }
  }, [currentJobId]);

  // Approve all matching entities with the same text (scope-aware reason application)
  const handleApproveWithScope = useCallback(async (index: number, reason: string | undefined, _scope: 'file' | 'case' | 'global') => {
    if (!currentJobId) return;

    // Find the clicked suggestion to get its text
    const currentSuggestions = suggestionsRef.current;
    const clickedSuggestion = currentSuggestions.find(s => s.index === index);
    if (!clickedSuggestion) return;

    // Find ALL suggestions with the same text
    const matchingIndices = currentSuggestions
      .filter(s => s.text === clickedSuggestion.text && s.status !== 'approved')
      .map(s => s.index);

    // Build reasons map for all matching entities
    const entityReasonsObj: Record<number, string> = {};
    if (reason) {
      matchingIndices.forEach(i => {
        entityReasonsObj[i] = reason;
        handleReasonChange(i, reason);
      });
    }

    // Optimistic update
    const prevSuggestions = currentSuggestions;
    const indexSet = new Set(matchingIndices);
    setSuggestions(prev =>
      prev.map(s =>
        indexSet.has(s.index)
          ? { ...s, status: 'approved' as const, replacementText: reason ? `[${reason}]` : s.replacementText }
          : s
      )
    );

    try {
      await redactionApi.approveSuggestions(currentJobId, {
        approved_entities: matchingIndices,
        entity_reasons: entityReasonsObj,
        apply_redactions: false,
      });
      loadEntityProgress();
    } catch (err) {
      console.error('Failed to approve suggestions with scope:', err);
      setSuggestions(prevSuggestions);
    }
  }, [currentJobId, handleReasonChange, loadEntityProgress]);

  // De-redact an approved suggestion (revert to pending), with optional scope
  const handleDeRedact = useCallback(async (index: number, scope: 'once' | 'file' | 'case' = 'once', word?: string) => {
    if (!currentJobId) return;

    // Compute matching indices for scope-based de-redact
    const currentSuggestions = suggestionsRef.current;
    const matchingIndices = (scope === 'file' || scope === 'case') && word
      ? currentSuggestions.filter(s => s.text === word && s.status === 'approved').map(s => s.index)
      : [index];

    // Optimistic update
    const prevSuggestions = suggestionsRef.current;
    const indexSet = new Set(matchingIndices);
    setSuggestions(prev =>
      prev.map(s =>
        indexSet.has(s.index)
          ? { ...s, status: 'pending' as const, replacementText: undefined }
          : s
      )
    );

    try {
      if (matchingIndices.length > 0) {
        await redactionApi.approveSuggestions(currentJobId, {
          approved_entities: [],
          pending_entities: matchingIndices,
          entity_reasons: {},
          apply_redactions: false,
        });
      }

      // Dictionary cleanup (stays after API success — irreversible side effect)
      if (scope === 'file' && word) {
        try {
          if (selectedDocumentId) {
            const fileEntries = await dictionaryApi.getForFile(selectedDocumentId);
            const toDelete = fileEntries.entries.filter(
              e => e.word.toLowerCase() === word.toLowerCase() && e.action === 'always_redact'
            );
            await Promise.all(toDelete.map(entry => dictionaryApi.deleteEntry(entry.id)));
          }
        } catch (dictErr) {
          console.warn('Failed to clean up file dictionary entries:', dictErr);
        }
        loadEntityProgress();
      } else if (scope === 'case' && word && selectedDocumentId && caseId) {
        try {
          const caseEntries = await dictionaryApi.getForCase(caseId);
          const toDelete = caseEntries.entries.filter(
            e => e.word.toLowerCase() === word.toLowerCase() && e.action === 'always_redact'
          );
          await Promise.all(toDelete.map(entry => dictionaryApi.deleteEntry(entry.id)));
        } catch (dictErr) {
          console.warn('Failed to clean up case dictionary entries:', dictErr);
        }
        loadEntityProgress();
      }
    } catch (err) {
      console.error('Failed to de-redact suggestion:', err);
      setSuggestions(prevSuggestions);
    }
  }, [currentJobId, selectedDocumentId, caseId, loadEntityProgress]);

  // Manual selection mode state for text-based documents
  const [isManualSelectionMode, setIsManualSelectionMode] = useState(false);
  // Active PDF drawing tool, lifted here so the selection persists across documents.
  const [activeTool, setActiveTool] = useState<RedactionTool>('blackout');

  // Handle manual redaction for PDF (bounding box or text selection)
  // Pending manual redaction awaiting type/reason selection in the modal.
  // `kind` distinguishes the PDF (bounds) path from the text-based path so we
  // can run the right persistence logic on confirm.
  const [pendingManualRedaction, setPendingManualRedaction] = useState<
    | { kind: 'pdf'; bounds: { x: number; y: number; width: number; height: number }; page: number; text?: string; position?: { x: number; y: number } }
    | { kind: 'text'; text: string; position?: { x: number; y: number } }
    | null
  >(null);

  // Holds the in-flight "what text is inside the drawn box?" request so the
  // popover interaction and the lookup run in parallel. Resolved when the user
  // confirms the manual redaction.
  const pendingRegionTextRef = useRef<Promise<TextInRegionResponse> | null>(null);

  // Values the entity picker collects and hands to the persistence helpers.
  type EntitySelection = { entity_type: EntityType; reason?: string };

  // Persist a manual PDF (bounding-box) redaction with the chosen type + reason.
  const persistManualPdfRedaction = useCallback(async (
    data: { bounds: { x: number; y: number; width: number; height: number }; page: number; text?: string },
    selection: EntitySelection,
  ) => {
    if (!currentJobId) {
      console.warn('No active redaction job - please run PII detection first');
      return;
    }

    const inlineText = data.text?.trim() || '';
    // A background region-text lookup was kicked off when the box was drawn.
    const regionPromise = pendingRegionTextRef.current;
    pendingRegionTextRef.current = null;
    const awaitingRegionText = !inlineText && !!regionPromise;

    // Optimistic: show the highlight immediately with a temporary index. If we're
    // still resolving the region text, mark it pending so the panel shows a spinner.
    const tempIndex = -(Date.now());
    const optimisticSuggestion = {
      index: tempIndex,
      entityType: selection.entity_type,
      text: inlineText || '[Manual Redaction]',
      confidence: 1.0,
      page: data.page,
      bounds: data.bounds,
      status: 'pending' as const,
      source: 'manual' as const,
      isOptimistic: true,
      textPending: awaitingRegionText,
    };
    setSuggestions(prev => [...prev, optimisticSuggestion]);

    // Resolve the region-text lookup before persisting, so the manual entity is
    // stored WITH its text and reprocessing can steer on it.
    let entityText = inlineText;
    if (awaitingRegionText && regionPromise) {
      try {
        const region = await regionPromise;
        entityText = region.text?.trim() || '';
      } catch (err) {
        console.error('Failed to extract text in region:', err);
        entityText = '';
      }
    }

    try {
      const response = await redactionApi.addManualEntity(currentJobId, {
        text: entityText,
        page: data.page,
        bounds: data.bounds,
        entity_type: selection.entity_type,
        reason: selection.reason && selection.reason !== 'N/A' ? selection.reason : undefined,
      });

      // Silently replace optimistic entry with real one from backend
      setSuggestions(prev => prev.map(s =>
        s.index === tempIndex
          ? { ...s, index: response.entity_index, entityType: (response.entity_type as EntityType) || selection.entity_type, text: response.text || entityText || '[Manual Redaction]', isOptimistic: undefined, textPending: false }
          : s
      ));
      setSelectedSuggestionIndex(prev => prev === tempIndex ? response.entity_index : prev);

      // Mirror the reason into local state so the UI reflects it without a refetch.
      if (selection.reason && selection.reason !== 'N/A') {
        handleReasonChange(response.entity_index, selection.reason);
      }
    } catch (err) {
      console.error('Failed to add manual redaction:', err);
      // Remove the optimistic entry on failure
      setSuggestions(prev => prev.filter(s => s.index !== tempIndex));
    }
  }, [currentJobId, handleReasonChange]);

  // Triggered by the PDF viewer: open the entity-selection modal instead of
  // creating the entity immediately.
  const handleManualPdfRedaction = useCallback((data: { bounds: { x: number; y: number; width: number; height: number }; page: number; reason?: string; text?: string; position?: { x: number; y: number } }) => {
    if (!currentJobId) {
      console.warn('No active redaction job - please run PII detection first');
      return;
    }
    // Area draws arrive without text; fetch what the document already has inside
    // the box in the background so it's ready by the time the user confirms.
    pendingRegionTextRef.current = data.text?.trim()
      ? null
      : redactionApi.getTextInRegion(currentJobId, { page: data.page, bounds: data.bounds });
    setPendingManualRedaction({ kind: 'pdf', bounds: data.bounds, page: data.page, text: data.text, position: data.position });
  }, [currentJobId]);

  // Handle page blackout toggle via dedicated endpoint
  const handleBlackoutPage = useCallback(async (page: number, reason?: string) => {
    if (!currentJobId) {
      console.warn('No active redaction job - please run PII detection first');
      return;
    }

    // Determine if this is a create or remove toggle
    const existing = suggestionsRef.current.find(s => s.text === '[Page Blackout]' && s.page === page && s.source === 'manual' && s.status !== 'rejected');
    const isRemoving = !!existing;

    // Optimistic update
    const tempIndex = -(Date.now());
    if (isRemoving) {
      setSuggestions(prev => prev.filter(s => s !== existing));
    } else {
      setSuggestions(prev => [...prev, {
        index: tempIndex,
        entityType: 'CUSTOM' as EntityType,
        text: '[Page Blackout]',
        confidence: 1.0,
        page,
        bounds: { x: 0, y: 0, width: 0, height: 0 },
        status: 'approved',
        source: 'manual',
        replacementText: reason ? `[${reason}]` : undefined,
        isOptimistic: true,
      }]);
    }

    try {
      const response = await redactionApi.blackoutPage(currentJobId, { page, reason });

      if (response.action === 'created') {
        // Replace optimistic entry with real one
        setSuggestions(prev => prev.map(s =>
          s.index === tempIndex
            ? { ...s, index: response.entity_index, page: response.page, isOptimistic: undefined }
            : s
        ));
        setSelectedSuggestionIndex(prev => prev === tempIndex ? response.entity_index : prev);
      }
      // For 'removed', optimistic delete already handled it
    } catch (err) {
      console.error('Failed to toggle page blackout:', err);
      // Rollback: restore removed entry or remove optimistic add
      if (isRemoving && existing) {
        setSuggestions(prev => [...prev, existing]);
      } else {
        setSuggestions(prev => prev.filter(s => s.index !== tempIndex));
      }
    }
  }, [currentJobId]);

  // Persist a manual text-based (DOCX/EML/MSG) redaction with chosen type + reason.
  const persistManualTextRedaction = useCallback(async (text: string, selection: EntitySelection) => {
    if (!currentJobId || !text.trim()) {
      setIsManualSelectionMode(false);
      return;
    }

    // Optimistic: show the highlight immediately with a temporary index
    const tempIndex = -(Date.now());
    setSuggestions(prev => [...prev, {
      index: tempIndex,
      entityType: selection.entity_type,
      text: text.trim(),
      confidence: 1.0,
      page: 0,
      status: 'pending',
      source: 'manual',
      isOptimistic: true,
    }]);

    try {
      const response = await redactionApi.addManualEntity(currentJobId, {
        text: text.trim(),
        page: 0, // Text-based files are single "page"
        entity_type: selection.entity_type,
        reason: selection.reason && selection.reason !== 'N/A' ? selection.reason : undefined,
      });

      // Silently replace optimistic entry with real one from backend
      setSuggestions(prev => prev.map(s =>
        s.index === tempIndex
          ? { ...s, index: response.entity_index, entityType: (response.entity_type as EntityType) || selection.entity_type, isOptimistic: undefined }
          : s
      ));
      setSelectedSuggestionIndex(prev => prev === tempIndex ? response.entity_index : prev);

      // Mirror the reason into local state so the UI reflects it without a refetch.
      if (selection.reason && selection.reason !== 'N/A') {
        handleReasonChange(response.entity_index, selection.reason);
      }
    } catch (err) {
      console.error('Failed to add manual text redaction:', err);
      // Remove the optimistic entry on failure
      setSuggestions(prev => prev.filter(s => s.index !== tempIndex));
    }
    // Stay in highlight mode so the reviewer can keep selecting text without
    // re-enabling the tool after every redaction (mirrors the PDF path).
  }, [currentJobId, handleReasonChange]);

  // Triggered by the text-based editors: open the entity-selection modal.
  const handleManualTextRedaction = useCallback((text: string, position?: { x: number; y: number }) => {
    if (!currentJobId || !text.trim()) {
      if (!currentJobId) {
        console.warn('No active redaction job - please run PII detection first');
      }
      setIsManualSelectionMode(false);
      return;
    }
    setPendingManualRedaction({ kind: 'text', text: text.trim(), position });
  }, [currentJobId]);

  // Confirm handler shared by both manual-redaction paths. The popover supplies
  // the (possibly user-edited) text plus the chosen entity type + reason.
  const handleConfirmEntitySelection = useCallback((data: { text: string; entityType: EntityType; reason?: string }) => {
    const pending = pendingManualRedaction;
    setPendingManualRedaction(null);
    if (!pending) return;
    const selection: EntitySelection = { entity_type: data.entityType, reason: data.reason };
    if (pending.kind === 'pdf') {
      void persistManualPdfRedaction({ bounds: pending.bounds, page: pending.page, text: data.text }, selection);
    } else {
      void persistManualTextRedaction(data.text, selection);
    }
  }, [pendingManualRedaction, persistManualPdfRedaction, persistManualTextRedaction]);

  // Cancel handler: discard the pending redaction but keep highlight mode on so
  // the reviewer can immediately reselect without re-enabling the tool.
  const handleCancelEntitySelection = useCallback(() => {
    setPendingManualRedaction(null);
  }, []);

  const handleNavigateToSuggestion = (suggestion: EntitySuggestion) => {
    setSelectedSuggestionIndex(suggestion.index);
  };

  // PST finalize handler - creates ZIP of redacted children
  const handlePstFinalize = useCallback(async () => {
    if (!selectedDocumentId || !caseId) return;

    setIsFinalizingPst(true);
    try {
      // Call the finalize endpoint which creates a ZIP of redacted children
      const result = await pstApi.finalize(selectedDocumentId);

      if (result.status === 'completed') {
        // Refresh case to get updated document status
        const response = await casesApi.getCase(caseId);
        setCaseData(response);
        setDocuments(response.documents);

        // Download the redacted ZIP file
        const { blob, filename } = await documentsApi.downloadDocument(
          selectedDocumentId!,
          'redacted',
          `redacted_${currentDocument?.name || 'archive.zip'}`
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Failed to finalize PST:', err);
    } finally {
      setIsFinalizingPst(false);
    }
  }, [selectedDocumentId, caseId, currentDocument]);

  // Finalize document: apply all approved redactions and mark as completed
  const handleFinalizeDocument = useCallback(async () => {
    if (!currentJobId || !selectedDocumentId || !caseId) return;

    // Check if there are any approved suggestions
    const approvedCount = suggestions.filter((s) => s.status === 'approved').length;
    if (approvedCount === 0) {
      console.warn('No approved suggestions to finalize');
      return;
    }

    setIsFinalizing(true);
    setFinalizeError(null);
    try {
      // Step 1: Apply all approved redactions to create the redacted file
      const result = await redactionApi.applyJobRedactions(currentJobId);

      if (result.status === 'completed' || result.status === 'no_changes') {
        // Step 2: Mark document as finalized (completed)
        await casesApi.finalizeDocument(caseId, selectedDocumentId);

        // Update local document state
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === selectedDocumentId ? { ...doc, status: 'completed' as DocumentStatus } : doc
          )
        );
        setCurrentDocument((prev) =>
          prev ? { ...prev, status: 'completed' as DocumentStatus } : prev
        );

        // Refresh case and documents to get updated status
        const response = await casesApi.getCase(caseId);
        setCaseData(response);
        setDocuments(response.documents);

        // Reload document to show redacted version
        if (currentDocument) {
          loadDocumentContent({ ...currentDocument, status: 'completed' as DocumentStatus });
        }
      }
    } catch (err) {
      console.error('Failed to finalize document:', err);
      const message = err instanceof Error ? err.message : 'Finalization failed';
      setFinalizeError(message);
    } finally {
      setIsFinalizing(false);
    }
  }, [currentJobId, selectedDocumentId, caseId, suggestions, currentDocument]);

  // Unfinalize document: allow re-editing by changing status back to 'redacted'
  const handleUnfinalize = useCallback(async () => {
    if (!selectedDocumentId || !caseId) return;

    try {
      await casesApi.updateDocumentStatus(caseId, selectedDocumentId, 'redacted');

      // Update local state
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === selectedDocumentId ? { ...doc, status: 'redacted' as DocumentStatus } : doc
        )
      );
      setCurrentDocument((prev) =>
        prev ? { ...prev, status: 'redacted' as DocumentStatus } : prev
      );

      // Refresh case and documents to get updated status
      const response = await casesApi.getCase(caseId);
      setCaseData(response);
      setDocuments(response.documents);
    } catch (err) {
      console.error('Failed to unfinalize document:', err);
    }
  }, [selectedDocumentId, caseId]);

  // Handle print - fetches the redacted PDF and opens browser print dialog
  const handlePrint = useCallback(async () => {
    if (!selectedDocumentId || !currentDocument) return;

    try {
      const { blob } = await documentsApi.downloadDocument(
        selectedDocumentId,
        'redacted',
        `redacted_${currentDocument.name}`,
        'pdf'
      );
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
          printWindow.addEventListener('afterprint', () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          });
        });
      } else {
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Failed to print document:', err);
    }
  }, [selectedDocumentId, currentDocument]);

  const handleAddToDictionary = useCallback(async (data: { word: string; scope: 'file' | 'case' | 'global'; action: 'always_redact' | 'always_allow' }) => {
    try {
      await dictionaryApi.createEntry({
        scope_type: data.scope,
        scope_id: data.scope === 'case' ? caseId : data.scope === 'file' ? (selectedDocumentId ?? undefined) : undefined,
        word: data.word,
        action: data.action,
        match_type: 'exact',
      });
      if (data.scope === 'file') {
        // File-scope rescan is synchronous — suggestions are ready immediately
        if (selectedDocumentId) {
          loadSuggestionsForDocument(selectedDocumentId);
        }
      } else {
        // Case/global rescan is async — start polling to detect when it completes
        loadEntityProgress();
      }
    } catch (err) {
      console.error('Failed to add to dictionary:', err);
    }
  }, [caseId, selectedDocumentId, loadSuggestionsForDocument, loadEntityProgress]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-[#7035C4]" />
          <p className="text-muted-foreground">Loading case...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !caseData) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <p className="text-destructive mb-4">{error || 'Case not found'}</p>
          <Button variant="outline" onClick={handleBack}>
            Back to Cases
          </Button>
        </div>
      </div>
    );
  }

  const fileType = currentDocument?.name.split('.').pop()?.toLowerCase();

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Top Bar */}
      <CaseTopBar
        caseData={caseData}
        onBack={handleBack}
        onUploadDocuments={handleUploadDocuments}
        onOpenCaseDict={caseId ? () => setIsCaseDictModalOpen(true) : undefined}
        onRenameCase={handleRenameCase}
        onDeleteCase={() => setIsDeleteDialogOpen(true)}
        onDownloadCase={handleDownloadCase}
        isDownloadDisabled={!documents.some(doc => doc.status === 'completed')}
        isDownloading={isDownloadingZip}
        onExportMegaPdf={documents.length > 0 ? handleExportMegaPdf : undefined}
        isExporting={isExportingPdf}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Documents Panel (Left) */}
        <DocumentsPanel
          documents={documents}
          selectedDocumentId={selectedDocumentId}
          onSelectDocument={setSelectedDocumentId}
          isCollapsed={isDocumentsPanelCollapsed}
          onToggleCollapse={() => setIsDocumentsPanelCollapsed(!isDocumentsPanelCollapsed)}
          onDownload={handleDownloadDocument}
          onOpenFileDict={caseId ? (docId) => setFileDictDocId(docId) : undefined}
          onDeleteDocument={handleDeleteDocument}
          onReprocessDocument={handleRequestReprocess}
          entityProgress={entityProgress}
          processingDocumentIds={processingDocumentIds}
          processingProgress={processingProgress}
          failedDocumentIds={failedDocumentIds}
        />

        {/* Document Viewer + Results Panel */}
        <div className={cn(
          'flex-1 flex overflow-hidden min-w-0',
          dockPosition === 'bottom' ? 'flex-col' : 'flex-row'
        )}>
          {/* Document Viewer (Center) */}
          <div className="flex-1 overflow-hidden bg-gray-50 min-h-0 min-w-0 flex flex-col">
            {isLoadingDocument ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="w-8 h-8 animate-spin text-[#7035C4]" />
                  <p className="text-muted-foreground">Loading document...</p>
                </div>
              </div>
            ) : documentError ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <FileX className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">{documentError}</p>
                </div>
              </div>
            ) : !selectedDocumentId ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <FileX className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-4">No document selected</p>
                  {documents.length === 0 && (
                    <Button
                      onClick={handleUploadDocuments}
                      className="bg-[#7035C4] hover:bg-[#5c2a9e] text-white"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Upload Documents
                    </Button>
                  )}
                </div>
              </div>
            ) : (fileType === 'pdf' && pdfData) ||
                 ((fileType === 'docx' || fileType === 'doc') && htmlContent) ||
                 ((fileType === 'eml' || fileType === 'msg') && htmlContent) ||
                 isContainerDocument ? (
              <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                {/* Document Header */}
                <div className="bg-card border-b border-border px-6 py-4 shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded">
                        {getFileIconByName(currentDocument?.name || '')}
                      </div>
                      <div>
                        <h3 className="font-medium">{currentDocument?.name || 'Document'}</h3>
                        <p className="text-sm text-muted-foreground">
                          {fileType === 'pdf' && 'PDF Document'}
                          {(fileType === 'eml' || fileType === 'msg') && 'Email'}
                          {(fileType === 'docx' || fileType === 'doc') && 'Word Document'}
                          {fileType === 'pst' && 'PST Archive'}
                          {fileType === 'zip' && 'ZIP Archive'}
                        </p>
                      </div>
                    </div>

                    {/* Header Actions */}
                    <div className="flex items-center gap-2">
                      {/* Print button - only show when finalized */}
                      {currentDocument?.status === 'completed' && (
                        <button
                          onClick={handlePrint}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-muted rounded-[var(--radius-button)] transition-colors"
                          title="Print (Ctrl+P)"
                        >
                          <Printer className="w-4 h-4" />
                          <span className="text-sm hidden sm:inline">Print</span>
                        </button>
                      )}

                      {/* Download button - only show when finalized */}
                      {currentDocument?.status === 'completed' && (
                        <div className="relative group">
                          <button
                            className="flex items-center gap-2 px-3 py-2 hover:bg-muted rounded-[var(--radius-button)] transition-colors"
                          >
                            <Download className="w-4 h-4" />
                            <span className="text-sm hidden sm:inline">Download</span>
                          </button>
                          <div className="absolute right-0 top-full mt-1 bg-white border border-border rounded-[var(--radius)] shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[120px]">
                            <button
                              onClick={() => handleDownload('original')}
                              className="block w-full px-4 py-2 text-sm text-left hover:bg-muted transition-colors"
                            >
                              Original
                            </button>
                            <button
                              onClick={() => handleDownload('redacted')}
                              className="block w-full px-4 py-2 text-sm text-left hover:bg-muted transition-colors"
                            >
                              Redacted
                            </button>
                            {['docx', 'doc', 'eml', 'msg'].includes(fileType || '') && (
                              <button
                                onClick={() => handleDownload('redacted', 'pdf')}
                                className="block w-full px-4 py-2 text-sm text-left hover:bg-muted transition-colors"
                              >
                                Redacted as PDF
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Edit button - only show when finalized, allows re-editing */}
                      {currentDocument?.status === 'completed' && (
                        <button
                          onClick={handleUnfinalize}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-muted rounded-[var(--radius-button)] transition-colors"
                          title="Edit redactions"
                        >
                          <Pencil className="w-4 h-4" />
                          <span className="text-sm hidden sm:inline">Edit</span>
                        </button>
                      )}

                      {/* Document Status Select - show when document is not finalized */}
                      {currentDocument?.status !== 'completed' && currentDocument?.status && (
                        <Select
                          value={currentDocument.status}
                          onValueChange={(value: string) => handleDocumentStatusChange(value as DocumentStatus)}
                        >
                          <SelectTrigger className="w-[180px]" size="sm">
                            <SelectValue placeholder="Status" />
                          </SelectTrigger>
                          <SelectContent>
                            {(['unprocessed', 'needs_redaction', 'redacted'] as DocumentStatus[]).map((status) => (
                              <SelectItem key={status} value={status}>
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: DOCUMENT_STATUS_CONFIG[status].color }}
                                  />
                                  {DOCUMENT_STATUS_CONFIG[status].label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      {/* Finalize button - different logic based on document type */}
                      {currentDocument?.status !== 'completed' && (isContainerDocument || currentJobId) && (
                        <button
                          onClick={isContainerDocument ? handlePstFinalize : handleFinalizeDocument}
                          disabled={
                            isFinalizing ||
                            isFinalizingPst ||
                            (isContainerDocument
                              ? !allChildrenCompleted
                              : suggestions.filter((s) => s.status === 'approved').length === 0 ||
                                (isEmailWithChildren && !allChildrenCompleted))
                          }
                          className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={
                            isContainerDocument
                              ? (!allChildrenCompleted
                                  ? 'Complete all child documents before finalizing'
                                  : 'Finalize and download redacted archive')
                              : isEmailWithChildren && !allChildrenCompleted
                                ? 'Complete all child attachments before finalizing'
                                : suggestions.filter((s) => s.status === 'approved').length === 0
                                  ? 'Approve at least one suggestion to finalize'
                                  : 'Apply redactions and finalize'
                          }
                        >
                          {(isFinalizing || isFinalizingPst) ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          <span>Finalize</span>
                        </button>
                      )}
                    </div>
                    {finalizeError && (
                      <p className="text-sm text-red-600 mt-1">{finalizeError}</p>
                    )}
                  </div>
                </div>

                {/* Editor Content */}
                <div className="flex-1 overflow-hidden min-h-0">
                  <Suspense fallback={<div className='flex items-center justify-center h-full'><Loader2 className='h-8 w-8 animate-spin text-muted-foreground' /></div>}>
                    {fileType === 'pdf' && pdfData ? (
                      <PDFEditor
                        pdfData={pdfData}
                        jobId={currentJobId}
                        suggestions={suggestions}
                        selectedSuggestionIndex={selectedSuggestionIndex}
                        onSelectSuggestion={setSelectedSuggestionIndex}
                        onManualRedactionAdd={handleManualPdfRedaction}
                        pendingManualBox={pendingManualRedaction?.kind === 'pdf'
                          ? { bounds: pendingManualRedaction.bounds, page: pendingManualRedaction.page }
                          : null}
                        reasons={reasons}
                        onApproveSuggestion={handleApproveWithReason}
                        onRejectSuggestion={handleRejectSingle}
                        onDeleteManualEntity={handleDeleteManualEntity}
                        onDeRedactSuggestion={handleDeRedact}
                        onAddToDictionary={handleAddToDictionary}
                        onApproveWithScope={handleApproveWithScope}
                        onRestoreToPending={handleRestoreToPending}
                        onChangeEntityType={handleChangeEntityType}
                        isManualSelectionMode={isManualSelectionMode}
                        onManualSelectionModeChange={setIsManualSelectionMode}
                        activeTool={activeTool}
                        onActiveToolChange={setActiveTool}
                        onBlackoutPage={handleBlackoutPage}
                        isReadOnly={currentDocument?.status === 'completed'}
                      />
                    ) : (fileType === 'docx' || fileType === 'doc') && htmlContent ? (
                      <DocxEditorView
                        htmlContent={htmlContent}
                        suggestions={suggestions}
                        selectedSuggestionIndex={selectedSuggestionIndex}
                        onSelectSuggestion={setSelectedSuggestionIndex}
                        isManualSelectionMode={isManualSelectionMode}
                        onManualSelectionModeChange={setIsManualSelectionMode}
                        onManualRedactionAdd={handleManualTextRedaction}
                        reasons={reasons}
                        onApproveSuggestion={handleApproveWithReason}
                        onRejectSuggestion={handleRejectSingle}
                        onDeRedactSuggestion={handleDeRedact}
                        onAddToDictionary={handleAddToDictionary}
                        onApproveWithScope={handleApproveWithScope}
                        onRestoreToPending={handleRestoreToPending}
                        onChangeEntityType={handleChangeEntityType}
                        isReadOnly={currentDocument?.status === 'completed'}
                      />
                    ) : (fileType === 'eml' || fileType === 'msg') && htmlContent ? (
                      <EmailEditorView
                        htmlContent={htmlContent}
                        suggestions={suggestions}
                        selectedSuggestionIndex={selectedSuggestionIndex}
                        onSelectSuggestion={setSelectedSuggestionIndex}
                        isManualSelectionMode={isManualSelectionMode}
                        onManualSelectionModeChange={setIsManualSelectionMode}
                        onManualRedactionAdd={handleManualTextRedaction}
                        reasons={reasons}
                        onApproveSuggestion={handleApproveWithReason}
                        onRejectSuggestion={handleRejectSingle}
                        onDeRedactSuggestion={handleDeRedact}
                        onAddToDictionary={handleAddToDictionary}
                        onApproveWithScope={handleApproveWithScope}
                        onRestoreToPending={handleRestoreToPending}
                        onChangeEntityType={handleChangeEntityType}
                        isReadOnly={currentDocument?.status === 'completed'}
                      />
                    ) : isContainerDocument ? (
                      <PSTEditorView
                        documentId={selectedDocumentId!}
                        childDocuments={childDocuments}
                        documentName={currentDocument?.name || ''}
                      />
                    ) : null}
                  </Suspense>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <FileX className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Preview not available</p>
                </div>
              </div>
            )}
          </div>

          {/* Results Panel (Bottom) - only show when there are suggestions or loading */}
          <Suspense fallback={null}>
          {(suggestions.length > 0 || isLoadingSuggestions) && (
            <ResultsPanel
              suggestions={suggestions}
              onApprove={handleApprove}
              onReject={handleReject}
              isCollapsed={isResultsPanelCollapsed}
              onToggleCollapse={toggleResultsPanelCollapse}
              isLoading={isLoadingSuggestions}
              onNavigate={handleNavigateToSuggestion}
              selectedSuggestionIndex={selectedSuggestionIndex}
              reasons={reasons}
              entityReasons={entityReasons}
              onReasonChange={handleReasonChange}
              onDeselectSuggestion={() => setSelectedSuggestionIndex(null)}
              dockPosition={dockPosition}
              onSetDockPosition={setDockPosition}
              panelSize={panelSize}
              isDragging={isResultsPanelDragging}
              handleMouseDown={handleResultsPanelMouseDown}
              panelRef={resultsPanelRef}
              onRestoreToPending={handleRestoreToPending}
              onApproveWithScope={handleApproveWithScope}
            />
          )}
          </Suspense>
        </div>
      </div>

      <Suspense fallback={null}>
        {/* Upload Documents Modal */}
        {caseData && (
          <CaseWizardModal
            open={isUploadModalOpen}
            onClose={() => setIsUploadModalOpen(false)}
            mode="add-documents"
            existingCase={caseData}
            onDocumentsAdded={handleDocumentsAdded}
          />
        )}

        {/* Case Dictionary Setup Modal */}
        {caseId && (
          <CaseDictionarySetupModal
            open={isCaseDictModalOpen}
            onClose={(hasChanges) => {
              setIsCaseDictModalOpen(false);
              if (hasChanges) {
                // Optimistically mark all documents as processing so the sidebar
                // shows "Analyzing..." immediately while the async rescan runs.
                // Don't call loadEntityProgress() here — it would overwrite the
                // optimistic set if the rescan already completed. The polling
                // effect (processingDocumentIds.size > 0 → 3s interval) will
                // pick up the real state and clear documents as they finish.
                setProcessingDocumentIds(new Set(documents.map((d) => d.id)));
              } else {
                loadEntityProgress();
              }
            }}
            scopeType="case"
            scopeId={caseId}
            scopeName={caseData?.name}
          />
        )}

        {/* File Dictionary Modal */}
        {fileDictDocId && (
          <CaseDictionarySetupModal
            open={!!fileDictDocId}
            onClose={(hasChanges) => {
              const docId = fileDictDocId;
              setFileDictDocId(null);
              if (hasChanges && docId) {
                setProcessingDocumentIds((prev) => new Set([...prev, docId]));
              }
              if (selectedDocumentId) {
                loadSuggestionsForDocument(selectedDocumentId);
              }
            }}
            scopeType="file"
            scopeId={fileDictDocId}
            scopeName={documents.find((d) => d.id === fileDictDocId)?.name}
          />
        )}
      </Suspense>

      {/* Delete Case Dialog */}
      <DeleteCaseDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        caseName={caseData?.name ?? ''}
        documentCount={caseData?.document_count ?? 0}
        onConfirm={handleDeleteCase}
        isDeleting={isDeletingCase}
      />

      <DeleteDocumentDialog
        open={!!deleteDocumentTarget}
        onOpenChange={(open) => { if (!open) setDeleteDocumentTarget(null); }}
        documentName={documents.find((d) => d.id === deleteDocumentTarget)?.name ?? ''}
        onConfirm={handleConfirmDeleteDocument}
        isDeleting={isDeletingDocument}
      />

      <ReprocessDocumentDialog
        open={!!reprocessTarget}
        onOpenChange={(open) => { if (!open) setReprocessTarget(null); }}
        documentName={documents.find((d) => d.id === reprocessTarget)?.name ?? ''}
        onConfirm={handleConfirmReprocessDocument}
        isReprocessing={!!reprocessTarget && processingDocumentIds.has(reprocessTarget)}
      />

      {/* Entity type + reason selection for manual redactions */}
      <EntityTypePickerPopover
        isOpen={!!pendingManualRedaction}
        position={pendingManualRedaction?.position ?? { x: window.innerWidth / 2 - 170, y: window.innerHeight / 2 - 200 }}
        extractedText={pendingManualRedaction?.text}
        page={pendingManualRedaction?.kind === 'pdf' ? pendingManualRedaction.page : undefined}
        reasons={reasons}
        onConfirm={handleConfirmEntitySelection}
        onCancel={handleCancelEntitySelection}
      />
    </div>
  );
}
